import { useState } from 'react';
import { X, Minus, Plus, Package } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import ProductGallery from '@/components/ProductGallery';

export default function ProductModal({ product, onClose, settings }) {
  const { addItem } = useCart();
  const variants = product?.variants || [];
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showSizeChart, setShowSizeChart] = useState(false);

  if (!product) return null;

  const selected = variants[selectedIdx];
  const gallery = product.gallery || [];
  const price = selected?.retail_price || '0.00';
  const selectedColor = selected?.color || '';
  const coverImage = gallery.find(g => g.is_cover)?.lg || gallery[0]?.lg || product.thumbnail_url;

  const handleAddToCart = () => {
    if (!selected) return;
    addItem({
      variant_id: selected.variant_id,
      product_id: product.id,
      name: product.name,
      variant_name: selected.name,
      price: price,
      currency: selected.currency || 'USD',
      image_url: coverImage,
      quantity,
    });
    onClose();
  };

  const hl = settings || {};
  const sizeChart = product.size_chart || '';
  const shippingNote = product.notes || 'Ships in 5–10 business days';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div data-testid="product-modal" className="fixed inset-2 sm:inset-8 md:inset-12 z-50 overflow-y-auto" style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', border: '1px solid var(--border-color)' }}>
        <button data-testid="close-product-modal" onClick={onClose} className="absolute top-4 right-4 p-2 z-10 transition-opacity hover:opacity-60" style={{ backgroundColor: 'var(--bg-color)', borderRadius: '50%' }}>
          <X size={20} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 min-h-full">
          {/* Gallery */}
          <div className="p-4 sm:p-6" style={{ backgroundColor: 'var(--card-bg, var(--bg-color))' }}>
            {gallery.length > 0 ? (
              <ProductGallery images={gallery} selectedColor={selectedColor} />
            ) : product.thumbnail_url ? (
              <img src={product.thumbnail_url} alt={product.name} className="w-full object-contain" style={{ maxHeight: '70vh' }} />
            ) : null}
          </div>

          {/* Details */}
          <div className="p-6 sm:p-8 md:p-10 flex flex-col">
            <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Product Detail</span>

            <h2 className="mt-3" style={{
              fontFamily: 'var(--headline-font)',
              fontStyle: hl.headline_italic !== false ? 'italic' : 'normal',
              fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
              textTransform: hl.headline_uppercase !== false ? 'uppercase' : 'none',
              lineHeight: 1.1,
            }}>
              {product.name}
            </h2>

            <p className="mt-3" style={{ fontFamily: 'var(--body-font)', fontSize: '1.5rem', fontWeight: 700 }}>${parseFloat(price).toFixed(2)}</p>

            {product.description && (
              <p className="mt-4" style={{ fontFamily: 'var(--body-font)', fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--muted-color)' }}>
                {product.description}
              </p>
            )}

            {/* Variant selector */}
            {variants.length > 1 && (
              <div className="mt-6">
                <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted-color)', fontWeight: 600 }}>
                  Option
                </span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {variants.map((v, i) => {
                    const label = v.name.split(' / ').slice(1).join(' / ') || v.name;
                    return (
                      <button
                        key={v.variant_id}
                        data-testid={`variant-option-${i}`}
                        onClick={() => setSelectedIdx(i)}
                        className="px-4 py-2 text-xs uppercase tracking-widest transition-all"
                        style={{
                          border: selectedIdx === i ? '2px solid var(--accent-color)' : '1px solid var(--border-color)',
                          backgroundColor: selectedIdx === i ? 'var(--accent-color)' : 'transparent',
                          color: selectedIdx === i ? '#fff' : 'var(--text-color)',
                          fontFamily: 'var(--body-font)',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6">
              <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted-color)', fontWeight: 600 }}>Quantity</span>
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center" style={{ border: '1px solid var(--border-color)' }}><Minus size={14} /></button>
                <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.9rem', minWidth: '2rem', textAlign: 'center' }}>{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className="w-9 h-9 flex items-center justify-center" style={{ border: '1px solid var(--border-color)' }}><Plus size={14} /></button>
              </div>
            </div>

            {/* Add to cart */}
            <Button
              data-testid="add-to-cart-modal"
              onClick={handleAddToCart}
              className="mt-6 w-full py-5 text-xs uppercase tracking-widest font-semibold"
              style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}
            >
              Add to Cart — ${(parseFloat(price) * quantity).toFixed(2)}
            </Button>

            {/* Size chart */}
            {sizeChart && (
              <>
                <button onClick={() => setShowSizeChart(!showSizeChart)} className="mt-4 text-left" style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', color: 'var(--muted-color)', textTransform: 'uppercase', letterSpacing: '0.1em', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  {showSizeChart ? 'Hide' : 'View'} Size Chart
                </button>
                {showSizeChart && (
                  <p className="mt-2 p-3" style={{ fontFamily: 'var(--body-font)', fontSize: '0.75rem', color: 'var(--muted-color)', border: '1px solid var(--border-color)', lineHeight: 1.6 }}>
                    {sizeChart}
                  </p>
                )}
              </>
            )}

            {/* Shipping note */}
            <div className="mt-4 flex items-center gap-2" style={{ color: 'var(--muted-color)' }}>
              <Package size={14} />
              <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem' }}>{shippingNote}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
